import {
    BackendPostCreditCard,
    BackendPostPaymentProfile,
    ECardStatus,
    ECardType,
    ICreditCardPaymentProfileItem,
    IDirectDebitPaymentProfileItem,
    IPaymentProfile,
    IPaymentProfileItem,
} from 'modules/PaymentProfile/reducers/type';
import { PaymentProfileState } from 'modules/PaymentProfile/reducers';
import * as telemetry from '../telemetry';
import { ETelemetryActionsAndLabels, ETelemetryNames } from 'telemetry/type';

export function isCreditCardPaymentProfile(
    paymentProfileItem: IPaymentProfileItem,
): paymentProfileItem is ICreditCardPaymentProfileItem {
    return paymentProfileItem.payment_method === ECardType.CreditCard;
}

export function isDirectDebitPaymentProfile(
    paymentProfileItem: IPaymentProfileItem,
): paymentProfileItem is IDirectDebitPaymentProfileItem {
    return paymentProfileItem.payment_method === ECardType.DirectDebit;
}

export function isOnAccountPaymentProfile(paymentProfile: IPaymentProfile): paymentProfile is IPaymentProfile {
    return paymentProfile.item.payment_method === ECardType.OnAccount;
}

export function isUnderPartnerPayAccount(paymentProfiles: IPaymentProfile[]): boolean {
    if (paymentProfiles.length != 1) {
        return false;
    }
    return paymentProfiles[0].is_partner_pay;
}

export function filterByStatus(paymentProfiles: IPaymentProfile[], status: ECardStatus): IPaymentProfile[] {
    return paymentProfiles?.filter(
        paymentProfile => 'item' in paymentProfile && paymentProfile.item?.status === status,
    );
}

export function isMultipleActivePaymentProfiles(paymentProfiles: IPaymentProfile[]): boolean {
    return filterByStatus(paymentProfiles, ECardStatus.Active).length > 1;
}

export function getSingleActivePaymentProfileIfThereIsOnlyOne(
    paymentProfiles: IPaymentProfile[],
): IPaymentProfile | null {
    return filterByStatus(paymentProfiles, ECardStatus.Active)[0] || null;
}

export function isPostCreditCard(detail: BackendPostPaymentProfile | {}): detail is BackendPostCreditCard {
    return 'last_four_digits' in detail;
}

export enum Permission {
    Denied = 'denied',
    Granted = 'granted',
}

export interface PaymentProfileDigest {
    isError: boolean;
    detailedError: string | null;
    isLoading: boolean;
    isOnAccount: boolean;
    isPartnerPay: boolean;
    isMultiActivePay: boolean;
    missesActivePaymentProfile: boolean;
    activePaymentProfile: IPaymentProfile | null;
    permission: Permission;
    permissionDeniedReason: Reason | null;
}

export type Reason = string;

export function getReasonForDenyingAccess(digest: PaymentProfileDigest): Reason | null {
    if (digest.isLoading) {
        return 'IsLoading';
    }
    if (digest.isOnAccount) {
        return 'OnAccount';
    }
    if (digest.isError) {
        return 'ErrorFetchingData';
    }
    if (digest.isMultiActivePay) {
        return 'MultipleActivePaymentProfiles';
    }
    if (digest.isPartnerPay) {
        return 'PartnerPays';
    }
    if (digest.missesActivePaymentProfile) {
        return 'NoPaymentProfileFound';
    }
    return null;
}

export function digestPaymentProfileData(state: PaymentProfileState): PaymentProfileDigest {
    const digest: PaymentProfileDigest = {
        isError: false,
        detailedError: null,
        missesActivePaymentProfile: true,
        activePaymentProfile: null,
        isLoading: false,
        isMultiActivePay: false,
        isOnAccount: false,
        isPartnerPay: false,
        permission: Permission.Denied,
        permissionDeniedReason: null,
    };
    if (state.error) {
        const res = { ...digest, isError: true };
        res.permissionDeniedReason = getReasonForDenyingAccess(res);
        if (state.error instanceof Error) {
            res.permissionDeniedReason += ': ' + state.error.message;
        } else {
            res.permissionDeniedReason += ': ' + state.error;
        }
        return res;
    }
    const paymentProfiles = state.items;
    if (paymentProfiles == null) {
        const res = { ...digest, isLoading: true };
        res.permissionDeniedReason = getReasonForDenyingAccess(res);
        return res;
    }

    if (isUnderPartnerPayAccount(paymentProfiles)) {
        const res = { ...digest, isPartnerPay: true };
        res.permissionDeniedReason = getReasonForDenyingAccess(res);
        return res;
    }

    const activePaymentProfile = getSingleActivePaymentProfileIfThereIsOnlyOne(paymentProfiles);
    if (activePaymentProfile == null) {
        const res = { ...digest, missesActivePaymentProfile: true };
        res.permissionDeniedReason = getReasonForDenyingAccess(res);
        return res;
    }

    digest.activePaymentProfile = activePaymentProfile;
    digest.missesActivePaymentProfile = false;

    digest.isOnAccount = isOnAccountPaymentProfile(activePaymentProfile);
    digest.isMultiActivePay = isMultipleActivePaymentProfiles(paymentProfiles);

    digest.permissionDeniedReason = getReasonForDenyingAccess(digest);
    if (digest.permissionDeniedReason == null) {
        digest.permission = Permission.Granted;
    }

    return digest;
}

export function trackDigest(eventName: ETelemetryNames, digest: PaymentProfileDigest) {
    telemetry.track(eventName, {
        action: ETelemetryActionsAndLabels.View,
        label: ETelemetryActionsAndLabels.View,
        viewData: { permission: digest.permission, reason: digest.permissionDeniedReason },
    });
}
